import axios from 'axios';
import {
  Paso1DatosPersonales,
  Paso2Contacto,
  VerificacionEmailRequest,
  VerificacionTelefonoRequest,
  VerificacionResponse
} from '../types/registro';

const API_BASE = '/api/registro-temporal';

export interface RegistroTemporal {
  sessionToken: string;
  paso1?: Paso1DatosPersonales;
  paso2?: Paso2Contacto;
  emailVerificado: boolean;
  // ❌ telefonoVerificado ya no se usa - teléfono es solo dato de contacto
  // telefonoVerificado: boolean;
  codigoEmailEnviado: boolean;
  // ❌ codigoTelefonoEnviado ya no se usa
  // codigoTelefonoEnviado: boolean;
  expiraEn: string; // ISO datetime
}

export interface CrearSesionTemporalResponse {
  success: boolean;
  mensaje: string;
  sessionToken: string;
  expiraEn: string;
}

export interface GuardarPaso1Response {
  success: boolean;
  mensaje: string;
  usernameDisponible: boolean;
  siguientePaso: string;
}

export interface GuardarPaso2Response {
  success: boolean;
  mensaje: string;
  emailDisponible: boolean;
  telefonoDisponible: boolean;
  codigoEmailEnviado: boolean;
  codigoTelefonoEnviado: boolean;
  siguientePaso: string;
  // Solo para desarrollo
  debugEmailCodigo?: string;
  debugTelefonoCodigo?: string;
}

// FinalizarRegistroResponse eliminado: ya no se necesita paso de finalización

class RegistroTemporalService {
  private sessionToken: string | null = null;

  // ==========================================================
  // GESTIÓN DE SESIÓN TEMPORAL
  // ==========================================================

  /**
   * Inicia una nueva sesión de registro temporal
   */
  async iniciarSesionTemporal(): Promise<CrearSesionTemporalResponse> {
    try {
      const response = await axios.post<CrearSesionTemporalResponse>(`${API_BASE}/iniciar-sesion`);
      
      if (response.data.success) {
        this.sessionToken = response.data.sessionToken;
        // Guardar en localStorage para persistencia
        localStorage.setItem('registroTemporalToken', this.sessionToken);
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Restaura sesión temporal desde localStorage
   */
  restaurarSesionTemporal(): boolean {
    const token = localStorage.getItem('registroTemporalToken');
    if (token) {
      this.sessionToken = token;
      return true;
    }
    return false;
  }

  /**
   * Limpia sesión temporal
   */
  limpiarSesionTemporal(): void {
    this.sessionToken = null;
    localStorage.removeItem('registroTemporalToken');
  }

  /**
   * Obtiene datos de la sesión temporal actual
   */
  async obtenerSesionTemporal(): Promise<RegistroTemporal | null> {
    if (!this.sessionToken) {
      return null;
    }

    try {
      const response = await axios.get<RegistroTemporal>(`${API_BASE}/sesion`, {
        params: { sessionToken: this.sessionToken }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting temporal session:', error);
      return null;
    }
  }

  // ==========================================================
  // PASOS DEL WIZARD
  // ==========================================================

  /**
   * Guarda datos del paso 1 temporalmente
   */
  async guardarPaso1(datos: Paso1DatosPersonales): Promise<GuardarPaso1Response> {
    if (!this.sessionToken) {
      throw new Error('No hay sesión temporal activa');
    }

    try {
      const response = await axios.post<GuardarPaso1Response>(`${API_BASE}/paso1`, {
        sessionToken: this.sessionToken,
        ...datos
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Guarda datos del paso 2 y envía códigos de verificación
   */
  async guardarPaso2(datos: { email: string; telefono: string }): Promise<GuardarPaso2Response> {
    if (!this.sessionToken) {
      throw new Error('No hay sesión temporal activa');
    }

    try {
      const response = await axios.post<GuardarPaso2Response>(`${API_BASE}/paso2`, {
        sessionToken: this.sessionToken,
        ...datos
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verifica código de email
   */
  async verificarEmail(data: VerificacionEmailRequest): Promise<VerificacionResponse> {
    if (!this.sessionToken) {
      throw new Error('No hay sesión temporal activa');
    }

    try {
      const response = await axios.post<VerificacionResponse>(`${API_BASE}/verificar-email`, {
        sessionToken: this.sessionToken,
        codigo: data.codigo,
        email: data.email
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verifica código de teléfono - ❌ DESHABILITADO
   * El teléfono ahora solo se captura como dato de contacto sin verificación
   * @deprecated No usar - la verificación de teléfono ha sido removida
   */
  async verificarTelefono(data: VerificacionTelefonoRequest): Promise<VerificacionResponse> {
    throw new Error('❌ verificarTelefono() está deshabilitado. La verificación de teléfono ya no es requerida.');
  }

  // ✅ La cuenta se crea automáticamente cuando se verifica el email

  // ==========================================================
  // REENVÍO DE CÓDIGOS
  // ==========================================================

  async reenviarEmailVerificacion(): Promise<any> {
    if (!this.sessionToken) {
      throw new Error('No hay sesión temporal activa');
    }

    try {
      const response = await axios.post(`${API_BASE}/reenviar-email`, {
        sessionToken: this.sessionToken
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reenviar código de teléfono - ❌ DESHABILITADO
   * @deprecated No usar - la verificación de teléfono ha sido removida
   */
  async reenviarTelefonoVerificacion(): Promise<any> {
    throw new Error('❌ reenviarTelefonoVerificacion() está deshabilitado. La verificación de teléfono ya no es requerida.');
  }

  // ==========================================================
  // UTILIDADES
  // ==========================================================

  getSessionToken(): string | null {
    return this.sessionToken;
  }

  hasActiveSession(): boolean {
    return this.sessionToken !== null;
  }
}

export const registroTemporalService = new RegistroTemporalService();
export default registroTemporalService;